import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTheme } from "@mui/material/styles";

import ActionButton from "../../components/ActionButton.jsx";
import StatusChip from "../../components/StatusChip.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { tokens } from "../../theme/tokens.js";
import { useResolvedTenantId } from "../../hooks/useResolvedTenantId.js";
import useGraphQLClient from "../../hooks/useGraphQLClient.js";

import { READ_TENANT_USERS_BY_TENANT } from "../../graph/tenantUsers/queries.js";
import { READ_HOLIDAY_REQUESTS } from "../../graph/holidayManagement/queries.js";
import {
  APPROVE_HOLIDAY_REQUEST,
  REJECT_HOLIDAY_REQUEST,
} from "../../graph/holidayManagement/mutations.js";

const formatMinutes = (minutes = 0) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours && remainingMinutes) {
    return `${hours}h ${remainingMinutes}m`;
  }

  if (hours) {
    return `${hours}h`;
  }

  return `${remainingMinutes}m`;
};

const formatDate = (date) => {
  if (!date) return "";

  try {
    return format(parseISO(date), "EEE d MMM yyyy");
  } catch {
    return date;
  }
};

function ManageHolidayRequests() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const client = useGraphQLClient();
  const { tenantId } = useResolvedTenantId();
  const { setSnackbar } = useSnackbar();

  const [requests, setRequests] = useState([]);
  const [staffNames, setStaffNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [processingRequestID, setProcessingRequestID] = useState("");

  const [rejectRequest, setRejectRequest] = useState(null);
  const [decisionComment, setDecisionComment] = useState("");

  const fetchRequests = useCallback(async () => {
    if (!tenantId) {
      setRequests([]);
      setStaffNames({});
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [requestsData, usersData] = await Promise.all([
        client.request(READ_HOLIDAY_REQUESTS, {
          filter: {
            tenantID: tenantId,
            status: "PENDING",
          },
        }),
        client.request(READ_TENANT_USERS_BY_TENANT, {
          tenantId,
        }),
      ]);

      const names = {};

      (usersData.readTenantUsersByTenant || []).forEach((tenantUser) => {
        const globalUser = tenantUser.user;
        const fullName =
          `${globalUser?.firstName || ""} ${globalUser?.lastName || ""}`.trim();

        names[tenantUser.id] =
          tenantUser.displayName ||
          fullName ||
          globalUser?.email ||
          "Staff member";
      });

      setStaffNames(names);
      setRequests(requestsData.readHolidayRequests || []);
    } catch (err) {
      console.error("Failed to load holiday requests:", err);
      setError("Unable to load holiday requests.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (request) => {
    setProcessingRequestID(request.id);

    try {
      await client.request(APPROVE_HOLIDAY_REQUEST, {
        id: request.id,
        decisionComment: null,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Holiday request approved",
      });

      await fetchRequests();
    } catch (err) {
      console.error("Failed to approve holiday request:", err);

      setSnackbar({
        open: true,
        severity: "error",
        message:
          err?.response?.errors?.[0]?.message ||
          "Failed to approve holiday request",
      });
    } finally {
      setProcessingRequestID("");
    }
  };

  const openRejectDialog = (request) => {
    setRejectRequest(request);
    setDecisionComment("");
  };

  const closeRejectDialog = () => {
    if (processingRequestID) return;

    setRejectRequest(null);
    setDecisionComment("");
  };

  const handleReject = async () => {
    const reason = decisionComment.trim();

    if (!rejectRequest || !reason) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Enter a reason for rejecting this request",
      });
      return;
    }

    setProcessingRequestID(rejectRequest.id);

    try {
      await client.request(REJECT_HOLIDAY_REQUEST, {
        id: rejectRequest.id,
        decisionComment: reason,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Holiday request rejected",
      });

      setRejectRequest(null);
      setDecisionComment("");
      await fetchRequests();
    } catch (err) {
      console.error("Failed to reject holiday request:", err);

      setSnackbar({
        open: true,
        severity: "error",
        message:
          err?.response?.errors?.[0]?.message ||
          "Failed to reject holiday request",
      });
    } finally {
      setProcessingRequestID("");
    }
  };

  if (!tenantId) {
    return (
      <Typography mt={3}>
        Select a tenant in the top bar to view staff holiday requests.
      </Typography>
    );
  }

  if (loading) {
    return <Typography mt={3}>Loading holiday requests...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" mt={3}>
        {error}
      </Typography>
    );
  }

  if (requests.length === 0) {
    return (
      <Typography mt={3}>
        There are no holiday requests waiting for approval.
      </Typography>
    );
  }

  return (
    <Box
      display="grid"
      gap={2}
      mt={2}
      gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
    >
      {requests.map((request) => (
        <Paper
          key={request.id}
          sx={{
            p: 2,
            border: `1px solid ${colors.scalpelTeal[400]}`,
            bgcolor: "background.paper",
          }}
        >
          <Box mb={1.5}>
            <StatusChip label="Pending approval" tone="warning" />
          </Box>

          <Typography variant="h5" mb={0.5}>
            {staffNames[request.staffTenantUserID] || "Staff member"}
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Submitted {formatDate(request.createdAt)}
          </Typography>

          <Typography fontWeight={700} mb={0.5}>
            Requested dates
          </Typography>

          <Box mb={1.5}>
            {(request.days || []).map((day) => (
              <Typography key={day.date} variant="body2">
                {formatDate(day.date)}
                {day.isPublicHoliday
                  ? " · Public holiday; no allowance deducted"
                  : ` · ${formatMinutes(day.chargedMinutes)} allowance`}
              </Typography>
            ))}
          </Box>

          <Typography variant="body2" mb={1}>
            Total allowance requested:{" "}
            <strong>{formatMinutes(request.totalChargedMinutes)}</strong>
          </Typography>

          {request.comment && (
            <Typography variant="body2" color="text.secondary" mb={2}>
              Staff comment: {request.comment}
            </Typography>
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1}
            flexWrap="wrap"
          >
            <ActionButton
              tone="danger"
              onClick={() => openRejectDialog(request)}
              disabled={Boolean(processingRequestID)}
            >
              Reject
            </ActionButton>

            <ActionButton
              tone="success"
              onClick={() => handleApprove(request)}
              disabled={Boolean(processingRequestID)}
            >
              {processingRequestID === request.id
                ? "Processing..."
                : "Approve"}
            </ActionButton>
          </Box>
        </Paper>
      ))}

      <Dialog
        open={Boolean(rejectRequest)}
        onClose={closeRejectDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject holiday request</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Add a reason so the staff member knows why the request was rejected.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason for rejection"
            value={decisionComment}
            onChange={(event) => setDecisionComment(event.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <ActionButton
            tone="ghost"
            onClick={closeRejectDialog}
            disabled={Boolean(processingRequestID)}
          >
            Cancel
          </ActionButton>

          <ActionButton
            tone="danger"
            onClick={handleReject}
            disabled={!decisionComment.trim() || Boolean(processingRequestID)}
          >
            {processingRequestID === rejectRequest?.id
              ? "Processing..."
              : "Reject request"}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ManageHolidayRequests;